import { cardArchiveId, cardDeleteId, cardDetailId, cardEditId, cardLinkId } from "@/assets/googleTrackIds";
import { productApis } from "@payping/api";
import { getProductsWithImage, removePermalink, removeProduct } from "@payping/redux/actions";
import { submitQueryParams } from "@payping/utils/query-string";
import { buildUrl } from "@payping/utilities-shared/url";
import { DashboardRoutes } from "@/router/constant";
import { CardList, Copy, Flex, Modal, Space, Typography, useMessagedAsyncRetry } from "@payping/web-components";
import qs from "query-string";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { DisplayModeEnum, ListCardProps } from "@payping/web-components/components/List/types";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import ProductFilter from "@/components/DashProduct/ProductListFilter";
import HandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import EmptyProductList from "./EmptyProductList";
import { ListBoolQueryParamsType, ListNumberQueryParamsType, ListStringQueryParamsType } from "./types";
import { ListBulkActions } from "./listBulkActions";
import Detail from "./Detail";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { URLS } from "@payping/utilities-shared/consts";

const Main: React.FC = () => {
  const [displayMode, setDisplayMode] = useState<DisplayModeEnum>(DisplayModeEnum.Card);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  // Because there are different types of filters, we should separate parsing them to avoid conflict between types (like converting "241e43" string to scientific number 2.41e45)
  const parsedSearch = qs.parse(location.search);
  const { searchText, searchDateFrom, searchDateTo }: ListStringQueryParamsType = qs.parse(location.search);
  const { pageSize = 12, pageNumber = 1 }: ListNumberQueryParamsType = qs.parse(location.search, {
    parseNumbers: true,
  });
  const { isArchived }: ListBoolQueryParamsType = qs.parse(location.search, { parseBooleans: true });
  const [detailCode, setDetailCode] = useState<string>();
  const products = useAppSelector((state) => state.products);
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [modal, contextHolder] = Modal.useModal();

  const { loading, retry } = useMessagedAsyncRetry(
    () =>
      dispatch(
        getProductsWithImage({
          limit: pageSize,
          offset: (pageNumber - 1) * pageSize,
          searchDateFrom,
          searchDateTo,
          searchText,
          withArchived: isArchived,
        }),
      ),
    [pageSize, pageNumber, searchText, isArchived, searchDateFrom, searchDateTo],
    { success: false, useProgress: true },
  );

  async function handleArchive(code?: string): Promise<void> {
    modal.confirm({
      cancelText: t("cancel"),
      content: isArchived ? t("product.confirm.DeArchive") : t("product.confirm.Archive"),
      maskClosable: true,
      okText: t("ok"),
      onOk: async () => {
        await productApis.productChangeArchive(code as string);
        await retry();
      },
    });
  }

  async function handleDelete(code?: string, havePerma?: boolean): Promise<void> {
    modal.confirm({
      cancelText: t("cancel"),
      content: t("product.remove.confirm"),
      maskClosable: true,
      okText: t("ok"),
      onOk: async () => {
        await dispatch(removeProduct(code as string));
        if (havePerma) await dispatch(removePermalink(code as string));
        await retry();
      },
    });
  }

  const data: ListCardProps["dataSource"] = products.list.map((product, id) => {
    const qrLink = buildUrl(URLS.PPNG_URL, "d", product.code || "").toString();

    return {
      actions: [
        <Space key={id} size={"middle"}>
          <Typography.Link onClick={() => setDetailCode(product.code)} data-google-track={cardDetailId}>
            {t("details")}
          </Typography.Link>
          <Link
            to={{
              pathname: DashboardRoutes.myShop.product.edit.url.replace(":code", product.code || ""),
            }}
            state={{ locationSearch: location.search }}
            data-google-track={cardEditId}
          >
            {t("edit")}
          </Link>
          <a
            onClick={async (event): Promise<void> => {
              event.stopPropagation();
              await handleArchive(product.code);
            }}
            data-google-track={cardArchiveId}
          >
            {!isArchived ? t("common.operation.archive") : t("common.operation.unArchive")}
          </a>
          <a
            onClick={async (event): Promise<void> => {
              event.stopPropagation();
              await handleDelete(product.code, product.havePerma);
            }}
            data-google-track={cardDeleteId}
          >
            {t("delete")}
          </a>
        </Space>,
      ],
      descriptions: (
        <Space direction={"vertical"} className={"tw-w-full"}>
          <Space size={4}>
            {t("quantity.title")}
            {product.unlimited ? t("quantity.unlimited") : formatLocalizedNumber({ value: product.quantity ?? 0 })}
          </Space>

          <Space size={4}>
            {t("product.amount")}
            {product.defineAmountByUser ? (
              t("product.form.price.byUser")
            ) : (
              <Flex align="center">
                <Typography.Text>{formatLocalizedNumber({ value: product.amount ?? 0 })}</Typography.Text>
                <CuToman width={24} height={24} />
              </Flex>
            )}
          </Space>

          {product.havePerma ? (
            <Space size={4}>
              {t("link.title")}
              <Typography.Link
                target={"_blank"}
                rel={"noreferrer"}
                onClick={(event) => event.stopPropagation()}
                href={qrLink}
                data-google-track={cardLinkId}
              >
                {qrLink.replace("https://", "")}
              </Typography.Link>
            </Space>
          ) : (
            /* size of cards needs to be equal */
            <Typography.Text className={"hidden"}>{t("link.title")}</Typography.Text>
          )}
        </Space>
      ),
      image: { src: product.imageLink, type: "image", size: "large" },
      key: product.code || "",
      title: {
        primary: product.title,
        secondary: <Copy text={product.code || ""}>{product.code}</Copy>,
        tag: product.category?.name,
      },
    };
  });
  const pagination: ListCardProps["pagination"] = {
    onChange: (page, newSize): void => {
      const newValues = {
        ...parsedSearch,
        pageNumber: page,
        pageSize: newSize,
      };
      setSelectedRowKeys([]);
      submitQueryParams({
        navigate,
        newValues,
        parsedSearch,
        pathname: location.pathname,
        retry,
      });
    },
    onShowSizeChange: (_currentPage, newSize): void => {
      const newValues = {
        ...parsedSearch,
        pageNumber: 1,
        pageSize: newSize,
      };
      submitQueryParams({
        navigate,
        newValues,
        parsedSearch,
        pathname: location.pathname,
        retry,
      });
    },
    pageNumber,
    pageSize,
    total: products.totalCount,
  };

  return (
    <HandlingCrispInMobile>
      <>
        {loading || data?.length > 0 || location.search ? (
          <Flex vertical gap={"middle"}>
            <ProductFilter
              loading={loading}
              retry={retry}
              displayMode={displayMode}
              setDisplayMode={setDisplayMode}
              setSelectedRowKeys={setSelectedRowKeys}
            />
            <CardList
              loading={loading}
              className={"tw-w-full"}
              pagination={pagination}
              grid={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 4 }}
              dataSource={data}
              itemSelection={{
                onChange: (selectedKeys) => {
                  setSelectedRowKeys(selectedKeys.map((r) => String(r)));
                },
                selectedItemKeys: selectedRowKeys,
              }}
              mode={displayMode}
            />
          </Flex>
        ) : (
          <EmptyProductList />
        )}
      </>
      <ListBulkActions
        retry={retry}
        isArchived={isArchived}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        data={data}
      />
      {detailCode ? <Detail code={detailCode} setCode={setDetailCode} retryList={retry} /> : null}

      {contextHolder}
    </HandlingCrispInMobile>
  );
};

export default Main;
